import type { DocumentOrMessage, DocumentsAndMessagesResponse, WorkbenchDocument, WorkbenchMessage } from "./api/types";

/** BFF document status that marks a document as openable in the viewer. */
const VIEWABLE_STATUS = "VIEWABLE";

/** Every message row matches this bucket — messages have no finer-grained category of their own. */
const MESSAGES_CATEGORY = "MESSAGES";

export interface DocumentsAndMessagesViewModel {
  /** Documents + messages unified and sorted newest → oldest. */
  items: DocumentOrMessage[];
  unreadMessageCount: number;
}

/**
 * Maps a document into the unified list item. Prefers the human `fileName`,
 * falling back to `docType` when the source doesn't supply one; `uploadedByOraid`
 * becomes the row author when present. Documents are never "unread" — only
 * messages carry a read/unread signal.
 * @param {WorkbenchDocument} document - A single document from the response.
 * @returns {DocumentOrMessage} The normalized list item.
 */
function mapDocument(document: WorkbenchDocument): DocumentOrMessage {
  const fileName = document.fileName ?? document.docType;
  return {
    id: document.docId,
    kind: "doc",
    name: fileName,
    receivedAt: document.uploadedAt ?? "",
    author: document.uploadedByOraid ?? undefined,
    unread: false,
    docId: document.docId,
    fileName,
    viewable: document.status === VIEWABLE_STATUS,
    reviewedBy: document.reviewedBy,
    pageCount: document.pageCount,
    filterCategory: document.filterCategory,
  };
}

/**
 * Maps a message into the unified list item. The `messages` leg is stubbed today,
 * so every field is read defensively. Only an explicit `read: false` marks the
 * row unread; a missing `read` field is treated as not-unread.
 * @param {WorkbenchMessage} message - A single message from the response.
 * @param {number} index - Position, used to synthesize a stable id when the BFF omits one.
 * @returns {DocumentOrMessage} The normalized list item.
 */
function mapMessage(message: WorkbenchMessage, index: number): DocumentOrMessage {
  return {
    id: message.messageId ?? `message-${index}`,
    kind: "message",
    name: message.subject ?? message.body ?? "Message",
    receivedAt: message.receivedAt ?? "",
    author: message.author ?? undefined,
    unread: message.read === false,
    filterCategory: MESSAGES_CATEGORY,
  };
}

/**
 * Whether a unified list item should be shown under the given filter
 * selection (SVRS-55873). Mirrors the BFF's `DocFilterClassifier` semantics:
 * `SHOW_ALL` matches everything; `FAXES_AND_MSGS` is the fax/misc document
 * bucket plus every message; `FAXES_AND_DOCS`/`MESSAGES` split those back
 * apart; the eSigned-variant filters (`ESIGNED_DOCS`/`R1_ESIGNED_DOCS`/
 * `DT_ESIGNED_DOCS`) match only a document whose own bucket equals the
 * filter.
 * @param {string} filter - The selected filter key (one of `meta.supportedFilters`).
 * @param {DocumentOrMessage} item - The unified list item to test.
 * @returns {boolean} Whether the item should be shown under this filter.
 */
export function matchesFilter(filter: string, item: DocumentOrMessage): boolean {
  switch (filter) {
    case "SHOW_ALL": {
      return true;
    }
    case "FAXES_AND_MSGS": {
      return item.kind === "message" || item.filterCategory === "FAXES_AND_DOCS";
    }
    case "MESSAGES": {
      return item.kind === "message";
    }
    // FAXES_AND_DOCS and the eSigned-variant filters (ESIGNED_DOCS/R1_ESIGNED_DOCS/DT_ESIGNED_DOCS)
    // all reduce to the same "doc whose own bucket matches" check.
    default: {
      return item.kind === "doc" && item.filterCategory === filter;
    }
  }
}

/**
 * Flattens the docs-messages response into a single newest-first list plus the
 * unread badge count. Sorting relies on the fixed-width `"YYYY-MM-DD HH:mm:ss"`
 * timestamp format, where lexical order equals chronological order.
 * @param {DocumentsAndMessagesResponse} response - The raw docs-messages payload.
 * @returns {DocumentsAndMessagesViewModel} The list + unread count for the panel.
 */
export function mapDocumentsAndMessages(response: DocumentsAndMessagesResponse): DocumentsAndMessagesViewModel {
  const { data } = response;
  const documents = data.documents ?? [];
  const messages = data.messages ?? [];

  const items: DocumentOrMessage[] = [
    ...documents.map((document) => mapDocument(document)),
    ...messages.map((message, index) => mapMessage(message, index)),
  ].sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));

  return {
    items,
    unreadMessageCount: data.unreadMessageCount ?? 0,
  };
}
